import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { ResetPasswordRequestDTO } from '../../adapters/backendDTOs';
import { userService } from '../../application/user';
import Alert from '../../components/common/alert';
import Button from '../../components/common/button';
import LoadingSpinner from '../../components/common/loadingSpinner';
import FormContainer from '../../components/forms/formContainer';
import Input from '../../components/forms/input';
import Layout from '../../components/layout/layout';
import { routes } from '../../domain/websiteDomain';
import useUserWithRedirects from '../../hooks/useUser';
import { passwordValidation } from '../../libs/text';

interface ResetPasswordProps {}

interface Values {
  email: string;
  password: string;
  confirm_password: string;
}

const schema = Yup.object({
  email: Yup.string().required('required'),
  password: passwordValidation,
  confirm_password: Yup.string()
    .required('required')
    .test('equal', 'passwords do not match', function (v) {
      const ref = Yup.ref('password');
      return v === this.resolve(ref);
    }),
});

const ResetPassword: React.FC<ResetPasswordProps> = () => {
  const [status, setStatus] = useState<
    'success' | 'error' | 'tokenExpired' | null
  >(null);
  const router = useRouter();
  const _ = useUserWithRedirects({
    redirectToIfFound: routes.FORGOT_PASSWORD,
  });

  const { register, handleSubmit, errors, formState } = useForm<Values>({
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values: Values) => {
    const applicationUser = userService();

    setStatus(null);

    const { token } = router.query;

    try {
      const data: ResetPasswordRequestDTO = {
        email: values.email,
        password: values.password,
        confirm_password: values.confirm_password,
        token: token as string,
      };
      await applicationUser.resetPassword(data);
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  });

  return (
    <Layout title="Reset password" classes="bg-background-lighter">
      <FormContainer title="Reset password">
        {(!status || status === 'error') && (
          <form onSubmit={onSubmit} className="space-y-8">
            <Input
              label="Email"
              name="email"
              ref={register}
              errors={errors}
              defaultValue={router.query.email}
            />
            <Input
              type="password"
              label="Password"
              ref={register}
              name="password"
              errors={errors}
            />
            <Input
              type="password"
              label="Confirm Password"
              name="confirm_password"
              ref={register}
              errors={errors}
            />

            {status === 'error' && (
              <Alert status={status}>
                There was an issue. Please try again.
              </Alert>
            )}

            <Button buttonStyle="primary" type="submit" classes="w-full">
              {formState.isSubmitting ? <LoadingSpinner /> : null}
              Reset Password
            </Button>
          </form>
        )}

        {status === 'tokenExpired' && (
          <div className="space-y-4">
            <Alert status="error">
              Your reset password token has expired. Please request to reset
              your password again.
            </Alert>
            <Button
              href={`/user/forgot-password${
                router.query.email ? `?email=${router.query.email}` : ''
              }`}
              buttonStyle="primary"
              classes="w-full"
            >
              Return to forgot password
            </Button>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <Alert status={status}>
              Nice one! Now please login with your new credentials.
            </Alert>
            <Button href="/user/login" buttonStyle="primary" classes="w-full">
              Proceed to login
            </Button>
          </div>
        )}
      </FormContainer>
    </Layout>
  );
};

export default ResetPassword;
